import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Calendar, CheckCircle, Clock, MapPin, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API } from '@/lib/config';

function formatDateTime(value) {
  return new Date(value).toLocaleString('pt-BR');
}

export default function EmailSelectionPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [request, setRequest] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [completed, setCompleted] = useState(null);

  useEffect(() => {
    fetchRequest();
  }, [token]);

  const groupedOptions = useMemo(() => {
    if (!request?.options) {
      return [];
    }
    return request.options;
  }, [request]);

  const fetchRequest = async () => {
    try {
      const response = await axios.get(`${API}/public/email-selection/${token}`);
      setRequest(response.data);
      if (response.data?.options?.length) {
        const first = response.data.options[0];
        setSelectedOption(`${first.room_id}|${first.slot_start}|${first.slot_end}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Nao foi possivel carregar a solicitacao');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!selectedOption) {
      toast.error('Selecione uma opcao');
      return;
    }

    const [room_id, slot_start, slot_end] = selectedOption.split('|');
    setSaving(true);
    try {
      const response = await axios.post(`${API}/public/email-selection/${token}/confirm`, {
        room_id,
        slot_start,
        slot_end
      });
      setCompleted(response.data);
      toast.success('Reserva confirmada com sucesso');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Nao foi possivel confirmar a reserva');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (completed) {
    const awaitingApproval = !completed.booking_id;
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <div className="max-w-2xl w-full bg-white rounded-3xl border border-border/40 p-10 shadow-xl text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-manrope font-bold tracking-tight mb-3">
            {awaitingApproval ? 'Solicitação enviada' : 'Reserva confirmada'}
          </h1>
          <p className="text-muted-foreground mb-6">
            {completed.message || (awaitingApproval
              ? 'A opção escolhida foi enviada para aprovação do administrador.'
              : 'A nova opção foi registrada e os participantes foram notificados por email.')}
          </p>
          <div className="text-left bg-gray-50 rounded-2xl p-5 space-y-2">
            <p><strong>Sala:</strong> {completed.room_name}</p>
            <p><strong>Inicio:</strong> {formatDateTime(completed.start_time)}</p>
            <p><strong>Fim:</strong> {formatDateTime(completed.end_time)}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <div className="max-w-xl w-full bg-white rounded-3xl border border-border/40 p-10 shadow-xl text-center">
          <h1 className="text-2xl font-manrope font-bold tracking-tight mb-3">Solicitacao indisponivel</h1>
          <p className="text-muted-foreground">O link pode ter expirado ou ja ter sido utilizado.</p>
        </div>
      </div>
    );
  }

  if (!groupedOptions.length) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <div className="max-w-2xl w-full bg-white rounded-3xl border border-border/40 p-10 shadow-xl text-center">
          <h1 className="text-2xl font-manrope font-bold tracking-tight mb-3">Sem opções disponíveis</h1>
          <p className="text-muted-foreground mb-4">
            As opções sugeridas para esta solicitação não estão mais livres neste momento.
          </p>
          <p className="text-sm text-muted-foreground">
            Solicite um novo link ou entre em contato com o administrador para gerar novas opções.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border/40 bg-white/60 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-6 flex items-center gap-3">
          <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
            <Mail className="w-6 h-6 text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-2xl font-manrope font-bold tracking-tight">Escolha sala e horario</h1>
            <p className="text-sm text-muted-foreground">Selecione uma das opcoes sugeridas para concluir sua reuniao</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-12 py-10 space-y-8">
        <div className="bg-white rounded-3xl border border-border/40 p-8 shadow-sm">
          <h2 className="text-3xl font-manrope font-bold tracking-tight mb-4">{request.meeting_name}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="rounded-2xl bg-gray-50 p-4">
              <p className="text-muted-foreground mb-1">Solicitante</p>
              <p className="font-medium">{request.organizer_email}</p>
            </div>
            <div className="rounded-2xl bg-gray-50 p-4">
              <p className="text-muted-foreground mb-1">Sala informada</p>
              <p className="font-medium">{request.requested_location || 'Nao informada'}</p>
            </div>
            <div className="rounded-2xl bg-gray-50 p-4">
              <p className="text-muted-foreground mb-1">Horario original</p>
              <p className="font-medium">{formatDateTime(request.original_start)} - {formatDateTime(request.original_end)}</p>
            </div>
            <div className="rounded-2xl bg-gray-50 p-4">
              <p className="text-muted-foreground mb-1">Participantes adicionais</p>
              <p className="font-medium">{request.attendees?.length || 0}</p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {groupedOptions.map((option) => {
            const value = `${option.room_id}|${option.slot_start}|${option.slot_end}`;
            const active = selectedOption === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() => setSelectedOption(value)}
                className={`w-full text-left bg-white rounded-2xl border p-6 transition-all ${
                  active ? 'border-primary shadow-lg ring-2 ring-primary/20' : 'border-border/40 hover:border-primary/40'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-primary" />
                      <span className="font-manrope font-semibold text-lg">{option.room_name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDateTime(option.slot_start)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="w-4 h-4" />
                      <span>Termina em {formatDateTime(option.slot_end)}</span>
                    </div>
                  </div>
                  {active && <CheckCircle className="w-6 h-6 text-primary flex-shrink-0" />}
                </div>
              </button>
            );
          })}
        </div>

        <Button onClick={handleConfirm} disabled={saving || !selectedOption} className="rounded-full h-12 px-8">
          {saving ? 'Confirmando...' : 'Confirmar opcao selecionada'}
        </Button>
      </main>
    </div>
  );
}
